/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import Button from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import LinkFilledIcon from '@atlaskit/icon/core/link';
import CheckCircleIcon from '@atlaskit/icon/core/status-success';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { G300 } from '@atlaskit/theme/colors';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

// TODO: delete below component if 'jfind-copy-link-modal-invite-tooltip' or 'jfind-copy-link-modal-invite-tooltip-jira' are set to true during cleanup
const InlineDialogContentWrapper = ({
	children,
	label,
}: {
	children: React.ReactNode;
	label?: string;
}) => (
	<Box aria-label={label} xcss={styles.inlineDialogContentWrapper}>
		{children}
	</Box>
);

const Z_INDEX = layers.modal();

const AUTO_DISMISS_MS = 8 * 1000;

const styles = cssMap({
	messageContainer: {
		display: 'flex',
		alignItems: 'center',
		marginTop: token('space.negative.100', '-8px'),
		marginRight: token('space.negative.200', '-16px'),
		marginBottom: token('space.negative.100', '-8px'),
		marginLeft: token('space.negative.200', '-16px'),
	},
	boxWrapper: {
		marginRight: token('space.025', '2px'),
	},
	messageText: {
		textIndent: token('space.075', '6px'),
	},
	inlineDialogContentWrapper: {
		paddingTop: token('space.200', '16px'),
		paddingRight: token('space.300', '24px'),
		paddingBottom: token('space.200', '16px'),
		paddingLeft: token('space.300', '24px'),
	},
	assistiveText: {
		border: 'none',
		// @ts-expect-error -- Type 'string' is not assignable to type 'undefined'.ts(2322)
		clip: 'rect(1px, 1px, 1px, 1px)',
		height: '1px',
		overflow: 'hidden',
		padding: '0',
		position: 'absolute',
		width: '1px',
		whiteSpace: 'nowrap',
	},
});

const isSafari = typeof window !== 'undefined' && window.navigator.userAgent.indexOf('Safari');

type InputProps = {
	label: string;
	text: string;
};

export const HiddenInput = React.forwardRef<HTMLInputElement, InputProps>(
	// we need a hidden input to reliably copy to clipboard across all browsers.
	(props, ref) => (
		<input
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={{ position: 'absolute', left: '-9999px' }}
			tabIndex={-1}
			aria-hidden={true}
			ref={ref}
			value={props.text}
			aria-label={props.label}
			readOnly
		/>
	),
);

export type Props = {
	children?: string | ReactElement;
	copiedToClipboardText: string;
	copyLinkButtonText: string;
	copyTooltipText?: string;
	link: string;
	onLinkCopy?: (link: string) => void;
};

export type State = {
	shouldShowCopiedMessage: boolean;
};

export const CopyLinkButton = ({
	copyLinkButtonText,
	children,
	onLinkCopy,
	link,
	copyTooltipText,
	copiedToClipboardText,
}: Props) => {
	const [shouldShowCopiedMessage, setShouldShowCopiedMessage] = useState(false);
	const autoDismiss = useRef<ReturnType<typeof setTimeout>>();
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => () => clearTimeout(autoDismiss.current), []);
	const clearAutoDismiss = useCallback(() => {
		if (autoDismiss.current) {
			clearTimeout(autoDismiss.current);
			autoDismiss.current = undefined;
		}
	}, []);

	const handleClick = useCallback(() => {
		inputRef.current?.select();
		document.execCommand('copy');
		if (onLinkCopy) {
			onLinkCopy(link);
		}
		setShouldShowCopiedMessage(true);
		clearAutoDismiss();
		autoDismiss.current = setTimeout(() => {
			setShouldShowCopiedMessage(false);
		}, AUTO_DISMISS_MS);
	}, [onLinkCopy, link, clearAutoDismiss]);

	const handleDismissCopiedMessage = useCallback(() => {
		clearAutoDismiss();
		setShouldShowCopiedMessage(false);
	}, [clearAutoDismiss]);

	const renderTriggerButton = useCallback(
		(triggerProps: TriggerProps) => (
			<Button
				aria-label={copyLinkButtonText}
				appearance="subtle"
				iconBefore={LinkFilledIcon}
				onClick={handleClick}
				ref={triggerProps.ref}
				spacing="default"
			>
				{children || copyLinkButtonText}
			</Button>
		),
		[copyLinkButtonText, handleClick, children],
	);

	const shouldUpdateTooltipCss =
		fg('jfind-copy-link-modal-invite-tooltip') || fg('jfind-copy-link-modal-invite-tooltip-jira');

	return (
		<React.Fragment>
			<Popup
				zIndex={Z_INDEX}
				autoFocus={false}
				content={() =>
					shouldUpdateTooltipCss ? null : (
						<InlineDialogContentWrapper>
							<Box xcss={cx(styles.messageContainer)} testId="message-container" aria-hidden>
								<CheckCircleIcon
									spacing="spacious"
									label=""
									color={token('color.icon.success', G300)}
								/>
								<Box xcss={cx(styles.messageText)}>{copiedToClipboardText}</Box>
							</Box>
						</InlineDialogContentWrapper>
					)
				}
				isOpen={shouldShowCopiedMessage}
				onClose={handleDismissCopiedMessage}
				placement="top-start"
				trigger={(triggerProps: TriggerProps) =>
					copyTooltipText && shouldShowCopiedMessage && shouldUpdateTooltipCss ? (
						<Tooltip content={copyTooltipText} position="bottom">
							{renderTriggerButton(triggerProps)}
						</Tooltip>
					) : (
						renderTriggerButton(triggerProps)
					)
				}
				shouldRenderToParent
			/>
			{/* Added ARIA live region specifically for VoiceOver + Safari */}
			{isSafari && (
				<div css={styles.assistiveText} aria-live="assertive">
					{shouldShowCopiedMessage && copiedToClipboardText}
				</div>
			)}
			<HiddenInput ref={inputRef} text={link} label={copiedToClipboardText} />
		</React.Fragment>
	);
};
